import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, IconButton, InputAdornment, Chip } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import Topbar from '../../components/topbar';
import SectionWrapper from '../../components/section';
import { useToast } from '../../providers/toaster-provider';

// No separate test/live toggle - Stripe keys self-declare their mode via
// prefix (pk_test_/sk_test_ vs pk_live_/sk_live_), so a manual switch would
// just be another thing that can disagree with what's actually pasted in.
// Mode is read directly off the secret key below instead.
const detectMode = (key: string): 'test' | 'live' | null => {
    if (key.startsWith('sk_test_') || key.startsWith('pk_test_')) return 'test';
    if (key.startsWith('sk_live_') || key.startsWith('pk_live_')) return 'live';
    return null;
};

// UI-only: a real Stripe secret key must never be stored in the browser
// (localStorage, etc.) even as a prototype - so unlike the Subscription
// Tiers admin page, this one deliberately does NOT persist anything. Values
// live only in this component's state and are gone on refresh/navigation,
// by design, until there's a real backend settings store to send them to.
const StripeSettingsAdmin: React.FC = () => {
    const { showSuccess } = useToast();
    const [publishableKey, setPublishableKey] = useState<string>('');
    const [secretKey, setSecretKey] = useState<string>('');
    const [showSecret, setShowSecret] = useState<boolean>(false);

    useEffect(() => { document.title = 'FineAnts Admin | Stripe Settings'; }, []);

    const handleSave = () => {
        // Nothing is actually sent or saved anywhere - see the note above.
        showSuccess('Saved');
    };

    const pubMode = detectMode(publishableKey);
    const secretMode = detectMode(secretKey);
    const mismatched = !!pubMode && !!secretMode && pubMode !== secretMode;

    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Stripe Settings" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', minHeight: 'calc(100vh - 86px)' }}>
                <SectionWrapper title="Payment Provider (Stripe)">
                    <Box display="flex" flexDirection="column" gap={3} maxWidth={520}>
                        <Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                                <Typography fontSize={14} fontWeight={500}>Publishable key</Typography>
                                {pubMode && (
                                    <Chip
                                        label={pubMode === 'test' ? 'Test' : 'Live'}
                                        size="small"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 11,
                                            backgroundColor: pubMode === 'test' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(26, 127, 55, 0.12)',
                                            color: pubMode === 'test' ? '#ed6c02' : '#1a7f37',
                                        }}
                                    />
                                )}
                            </Box>
                            <TextField
                                placeholder="pk_live_... or pk_test_..."
                                fullWidth
                                size="small"
                                value={publishableKey}
                                onChange={(e) => setPublishableKey(e.target.value)}
                            />
                        </Box>

                        <Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                                <Typography fontSize={14} fontWeight={500}>Secret key</Typography>
                                {secretMode && (
                                    <Chip
                                        label={secretMode === 'test' ? 'Test' : 'Live'}
                                        size="small"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 11,
                                            backgroundColor: secretMode === 'test' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(26, 127, 55, 0.12)',
                                            color: secretMode === 'test' ? '#ed6c02' : '#1a7f37',
                                        }}
                                    />
                                )}
                            </Box>
                            <TextField
                                placeholder="sk_live_... or sk_test_..."
                                fullWidth
                                size="small"
                                type={showSecret ? 'text' : 'password'}
                                value={secretKey}
                                onChange={(e) => setSecretKey(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowSecret(v => !v)} edge="end">
                                                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {mismatched && (
                            <Typography fontSize={12} color="#d32f2f">
                                These keys don't match - the publishable key is {pubMode} mode but the secret key is {secretMode} mode.
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', maxWidth: 520 }}>
                        <Button variant="contained" sx={{ color: '#fff' }} onClick={handleSave}>
                            Save Changes
                        </Button>
                    </Box>
                </SectionWrapper>
            </div>
        </div>
    );
};

export default StripeSettingsAdmin;
